import React, { FC, useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { authPages } from '../../../menu';
import AuthContext from '../../../contexts/authContext';
import { store } from '../../../store/store';
import Icon from '../../../components/icon/Icon';
import BgImage from '../../../assets/bg-image.png';
import AivisLogo from '../../../assets/svg/logo1.svg';

export interface LocationStateInterface {
	from: string;
	message?: string;
}

const Login: FC = () => {
	const { setUser } = useContext(AuthContext);
	const dispatch = useDispatch<any>();
	const navigate = useNavigate();
	const { t } = useTranslation(['authPage']);
	const [focusedField, setFocusedField] = useState<string | null>(null);
	const [showPassword, setShowPassword] = useState(false);

	// Lock body scroll while login is mounted
	useEffect(() => {
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = prev;
		};
	}, []);

	const formik = useFormik({
		initialValues: { loginUsername: '', loginPassword: '' },
		validate: (values) => {
			const errors: { loginUsername?: string; loginPassword?: string } = {};
			if (!values.loginUsername) errors.loginUsername = t('Required');
			if (!values.loginPassword) errors.loginPassword = t('Required');
			return errors;
		},
		validateOnChange: false,
		onSubmit: async (values) => {
			await dispatch.auth
				.getLogin({ userid: values.loginUsername, password: values.loginPassword })
				.then(() => {
					const { auth } = store.getState();
					if (auth.success) {
						if (setUser) setUser(localStorage.getItem('token') || '');
						navigate('overview');
					} else {
						formik.setFieldError(
							'loginPassword',
							auth.message || t('Invalid credentials'),
						);
					}
				})
				.catch(() => {
					formik.setFieldError('loginPassword', t('Login failed. Please try again.'));
				});
		},
	});

	const inputSt = (field: string): React.CSSProperties => ({
		width: '100%',
		padding: '13px 16px',
		background: 'rgba(255,255,255,0.07)',
		border: `1px solid ${
			formik.errors[field as keyof typeof formik.errors] && formik.submitCount > 0
				? 'rgba(244,67,54,0.8)'
				: focusedField === field
				? 'rgba(240,13,105,0.8)'
				: 'rgba(240,13,105,0.3)'
		}`,
		borderRadius: 10,
		color: 'black',
		fontSize: 14,
		outline: 'none',
		boxSizing: 'border-box',
		transition: 'border-color 0.2s, box-shadow 0.2s',
		boxShadow: focusedField === field ? '0 0 0 3px rgba(240,13,105,0.15)' : 'none',
	});

	return (
		<>
			<div
				style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					zIndex: 9999,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					overflow: 'hidden',
				}}>
				<img
					src={BgImage}
					alt=''
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						objectFit: 'cover',
						objectPosition: 'center center',
						zIndex: 0,
						filter: 'brightness(1.15)',
						display: 'block',
					}}
				/>
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						zIndex: 1,
					}}
				/>

				<div
					style={{
						position: 'relative',
						zIndex: 2,
						width: 'clamp(360px, 30vw, 580px)',
						padding: 'clamp(24px, 2.8vw, 64px) clamp(24px, 2.8vw, 48px)',
						background: 'rgba(255, 255, 255, 0.75)',
						backdropFilter: 'blur(16px)',
						WebkitBackdropFilter: 'blur(16px)',
						borderRadius: 16,
						boxSizing: 'border-box',
					}}>
					{/* Logo */}
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							marginBottom: 'clamp(24px, 2vw, 40px)',
						}}>
						<img src={AivisLogo} alt='Aivis Logo' style={{ height: 45, width: 'auto', marginBottom: 10 }} />
						<p style={{ color: 'black', fontSize: 13, margin: 0 }}>
							AI-FIRST FLEET INTELLIGENCE PLATFORM
						</p>
					</div>

					<h2
						style={{
							color: 'black',
							fontSize: 22,
							fontWeight: 700,
							marginBottom: 'clamp(22px, 1.8vw, 36px)',
							textAlign: 'center',
						}}>
						Sign In
					</h2>

					<form
						onSubmit={formik.handleSubmit}
						style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
						<div>
							<label
								htmlFor='loginUsername'
								style={{
									display: 'block',
									color: 'black',
									fontSize: 13,
									fontWeight: 400,
									marginBottom: 6,
								}}>
								Email Address
							</label>
							<input
								id='loginUsername'
								name='loginUsername'
								type='text'
								value={formik.values.loginUsername}
								onChange={formik.handleChange}
								onFocus={() => setFocusedField('loginUsername')}
								onBlur={() => setFocusedField(null)}
								placeholder='Enter your username or email'
								autoComplete='username'
								style={inputSt('loginUsername')}
							/>
							{formik.errors.loginUsername && formik.submitCount > 0 && (
								<p
									style={{
										color: 'black',
										fontSize: 12,
										margin: '4px 0 0',
										fontWeight: 400,
										textAlign: 'right',
									}}>
									⚠️ {formik.errors.loginUsername}
								</p>
							)}
						</div>

						<div>
							<label
								htmlFor='loginPassword'
								style={{
									display: 'block',
									color: 'black',
									fontSize: 13,
									fontWeight: 400,
									marginBottom: 6,
								}}>
								Password
							</label>
							<div style={{ position: 'relative' }}>
								<input
									id='loginPassword'
									name='loginPassword'
									type={showPassword ? 'text' : 'password'}
									value={formik.values.loginPassword}
									onChange={formik.handleChange}
									onFocus={() => setFocusedField('loginPassword')}
									onBlur={() => setFocusedField(null)}
									placeholder='Enter your password'
									autoComplete='current-password'
									style={{ ...inputSt('loginPassword'), paddingRight: 44 }}
								/>
								<button
									type='button'
									onClick={() => setShowPassword((prev) => !prev)}
									aria-label={showPassword ? 'Hide password' : 'Show password'}
									tabIndex={-1}
									style={{
										position: 'absolute',
										top: '50%',
										right: 12,
										transform: 'translateY(-50%)',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										background: 'none',
										border: 'none',
										padding: 4,
										margin: 0,
										cursor: 'pointer',
										color: 'rgba(0,0,0,0.45)',
										lineHeight: 0,
									}}>
									<Icon icon={showPassword ? 'Visibility' : 'VisibilityOff'} size='lg' />
								</button>
							</div>
							{formik.errors.loginPassword && formik.submitCount > 0 && (
								<p
									style={{
										color: 'black',
										fontSize: 12,
										margin: '4px 0 0',
										fontWeight: 400,
										textAlign: 'right',
									}}>
									⚠️ {formik.errors.loginPassword}
								</p>
							)}
						</div>

						<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -4 }}>
							<Link
								to={`/${authPages.forgetPassword.path}`}
								style={{ color: 'black', fontSize: 12, textDecoration: 'none' }}>
								Forgot Password?{' '} 
								  <span style={{ color: '#f00d69', cursor: 'pointer', fontWeight: 600 }}>
                          Click here
                        </span>
							</Link>
						</div>

						<button
							type='submit'
							disabled={formik.isSubmitting}
							style={{
								width: '100%',
								padding: '13px',
								background: formik.isSubmitting
									? 'rgba(240,13,105,0.4)'
									: 'linear-gradient(135deg,#f00d69,#c00050)',
								border: 'none',
								borderRadius: 10,
								color: '#fff',
								fontWeight: 700,
								fontSize: 15,
								cursor: formik.isSubmitting ? 'not-allowed' : 'pointer',
								marginTop: 4,
								letterSpacing: 0.5,
								boxShadow: formik.isSubmitting
									? 'none'
									: '0 4px 20px rgba(240,13,105,0.35)',
							}}>
							{formik.isSubmitting ? '⏳ Signing in...' : 'SIGN IN'}
						</button>
					</form>
				</div>
			</div>

			<style>{`
				input::placeholder { color: 'black', !important; }
				@media (max-width: 480px) {
					input[type="text"], input[type="password"] { font-size: 16px !important; }
				}
			`}</style>
		</>
	);
};

export default Login;